import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone, signal } from '@angular/core';
import { AuthService } from '@core/services/auth/auth.service';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { EventSource } from 'eventsource';
import { Notification } from '../interface/notification.interface';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private auth: AuthService
  ) {}

  isNotificationDialogVisible: boolean = false;

  // Signal to store selected notification for cross-component communication
  selectedNotification = signal<Notification | null>(null);

  setNotification(body: any = {}, params: any = {}): Observable<object> {
    return this.http.post('/api/notifications', body, { params });
  }

  // get server-sent events for notifications
  getNewNotifications(): Observable<any> {
    const url = environment.protocol + "://" + environment.host + '/api/notifications/sse';
    const token = String(this.auth.getJWTLocalStorage()).replace(/['"]+/g, "");
    return new Observable((observer) => {
      const eventSource = new EventSource(url,{
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: {
              ...init?.headers,
              Authorization: `Bearer ${token}`,
            },
          }),
      });
      eventSource.onmessage = (event) => {
        this.zone.run(() => {
          observer.next(JSON.parse(event.data));
        });
      };
      eventSource.onerror = (error) => {
        this.zone.run(() => {
          observer.error(error);
        });
      };
      return () => eventSource.close();
    });
  }

  getAllNotifications(status:String = 'all', page:Number = 1, limit:Number = 10, context:String = 'all'): Observable<any> {
    let url;
    if(context === 'all') {
      url = `/api/notifications?status=${status}&page=${page}&limit=${limit}`
    } else {
      url = `/api/notifications/?status=${status}&page=${page}&limit=${limit}&context=${context}`
    }
    return this.http.get(url);
  }

  markNotificationAsViewed(params:any = {}):Observable<any> {
    return this.http.get('/api/notifications/marked-viewed/' + params.id);
  }

  /**
   * Handle notification click and set selected notification
   * @param notification - The notification that was clicked
   */
  handleNotificationClick(notification: Notification): void {
    this.selectedNotification.set(notification);
    this.isNotificationDialogVisible = false;
  }

  /**
   * Clear selected notification
   */
  clearSelectedNotification(): void {
    this.selectedNotification.set(null);
  }

  /**
   * Get navigation route based on notification context
   * @param context - Notification context
   * @returns Route path
   */
  getNavigationRoute(context: string): string {
    const routeMap: { [key: string]: string } = {
      'claim_comment': '/claims/manage',
      'report_comment': '/reports-iq/summary',
      'claim_status_change': '/claims/manage',
      'claim_assignment': '/claims/manage',
      'shared': '/',
      'workspace': '/',
      'comment': '/',
      'reminder': '/'
    };
    return routeMap[context] || '/';
  }
}
